import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import moment from "moment";
import {compose} from "mu-lambda";
import * as React from "react";
import {useEffect, useRef} from "react";
import {Helmet} from "react-helmet-async";

import {
    Breadcrumbs,
    BreadcrumbsItem,
} from "@khanacademy/wonder-blocks-breadcrumbs";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {
    semanticColor,
    sizing,
    spacing,
} from "@khanacademy/wonder-blocks-tokens";
import {
    Body,
    HeadingLarge,
    HeadingSmall,
} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    usePublishAnalyticsEvent,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {useQuery} from "@khan/data-gql";
import {GuideColors} from "@khan/guide-ui";

import ConversationView from "../conversation-view.tsx";
import MaybeResumeLink from "../maybe-resume-link.tsx";

import {GUIDE_TRANSCRIPT_QUERY} from "./guide-transcript-query.ts";

import externalLinkIcon from "@phosphor-icons/core/bold/arrow-square-out-bold.svg";

interface OwnProps {
    profilePath: string;
    relationship: "SELF" | "COACH" | "DISTRICT_ADMIN" | "OTHER";
    transcriptId: string;
}

type Props = OwnProps & WithMarkConversionProps;

const GuideTranscript = ({
    markConversion,
    transcriptId,
    profilePath,
    relationship,
}: Props): React.ReactElement => {
    useOnMountEffect(() => {
        markConversion({id: "guide_transcript_page_view"});
    });

    const [result] = useQuery(GUIDE_TRANSCRIPT_QUERY, {
        variables: {transcriptId},
    });

    const thread =
        result.status === "success" ? result.data?.aiGuideThread : null;
    const title = thread
        ? thread.title ||
          t`Khanmigo chat from ${moment(thread.lastUpdatedAt).format("l LT")}`
        : t`Khanmigo Chat`;

    const publishAnalyticsEvent = usePublishAnalyticsEvent();
    const loggedOnMountRef = useRef(false);
    useEffect(() => {
        // We only want to log this event once, when the thread is
        // available.
        if (!thread) {
            return;
        }
        if (!loggedOnMountRef.current) {
            loggedOnMountRef.current = true;
            publishAnalyticsEvent({
                eventType: "AiGuideHistoryThreadSelected",
                eventSchemaVersion: 3,
                openedTranscriptThreadId: transcriptId,
                reviewerRelationship: relationship,
                isFlaggedByModeration: thread.flagged,
            });
        }
    }, [publishAnalyticsEvent, thread, transcriptId, relationship]);

    let contents: React.ReactNode = null;

    let displayTitle = true;
    if (result.status === "loading" || result.status === "no-data") {
        contents = <CircularSpinner />;
        displayTitle = false;
    } else if (result.status !== "success" || !thread) {
        const href = "/about/privacy-policy";

        const linkComponent = (
            <Link
                href={href}
                // We set tabbing to -1 so users will be sent to the next
                // element in the tabbing order outside of this menu on tab.
                tabIndex={-1}
                target="_blank"
                endIcon={<PhosphorIcon icon={externalLinkIcon} size="small" />}
            >
                {t`Privacy Policy`}
            </Link>
        );
        contents = (
            <View style={styles.noChatContainer}>
                <HeadingSmall>
                    {t`This chat is no longer available.`}
                </HeadingSmall>
                <Body tag="p" style={styles.noChatBody}>
                    <span>
                        {t`It has been removed from chat history in accordance with our deletion practices`}
                        : &nbsp;
                        {linkComponent}
                    </span>
                </Body>
            </View>
        );
        displayTitle = false;
    } else {
        contents = (
            <>
                <MaybeResumeLink
                    buttonKind="tertiary"
                    onClassroomExperience={false}
                    thread={thread}
                />
                <ConversationView
                    thread={thread}
                    userChatStyle={styles.purpleMessage}
                />
            </>
        );
    }

    return (
        <View>
            <Helmet>
                <title>{title}</title>
            </Helmet>
            <Breadcrumbs>
                <BreadcrumbsItem>
                    <Link href={profilePath}>{t`Profile`}</Link>
                </BreadcrumbsItem>
                <BreadcrumbsItem>
                    <Link href={`${profilePath}/khanmigo/history`}>
                        {t`Khanmigo Chat History`}
                    </Link>
                </BreadcrumbsItem>
            </Breadcrumbs>
            {displayTitle && (
                <HeadingLarge
                    style={{
                        marginBlockStart: spacing.xLarge_32,
                        marginBlockEnd: spacing.small_12,
                    }}
                >
                    {title}
                </HeadingLarge>
            )}
            {contents}
        </View>
    );
};

type ExportProps = WithoutMarkConversion<
    JSX.LibraryManagedAttributes<
        typeof GuideTranscript,
        React.ComponentProps<typeof GuideTranscript>
    >
>;

const GuideTranscriptsWithComposed = compose(
    withConversionContext({
        activity: enums.Activity.OTHER_ACTIVITY,
    }),
    withMarkConversion,
)(GuideTranscript) as React.ComponentType<ExportProps>;
export {GuideTranscriptsWithComposed as GuideTranscript};

const styles = StyleSheet.create({
    noChatContainer: {
        alignItems: "center",
        background: semanticColor.core.background.neutral.subtle,
        // Default bottom-margin on a `<p>` tag is 22px.  We want a total
        // bottom-padding of 40px.  22px - 40px = 18px.  It was either this or
        // set the bottom-margin of the `<p>` to 0px.
        padding: "40px 0 18px",
        textAlign: "center",
        marginBlockEnd: spacing.xxLarge_48,
        marginBlockStart: sizing.size_080,
    },
    noChatBody: {
        display: "flex",
        marginBlockStart: spacing.xSmall_8,
    },
    purpleMessage: {
        backgroundColor: GuideColors.darkPurple,
        color: semanticColor.core.foreground.knockout.default,
    },
});
